import { SaveOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { Col, Flex, Form, Input, Row, Select, Switch, Tooltip } from "antd";
import { get } from "lodash";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { setLoading } from "../../../redux/ducks/Loading";
import { setSection } from "../../../redux/ducks/Section";
import ClientPortal from "../../../services/api/ClientPortal";
import { showError, showSuccess } from "../../../utils/utilities";
import PFButton from "../../../components/functional_components/Button/PFButton";

const ClientsDetail = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const params = useParams();
  const [formCLientsDetail] = Form.useForm();
  const [isFormValid, setIsFormValid] = useState(false);
  const [state, setState] = useState({
    client: {
      id: null,
      name: null,
      group: null,
      billingCountry: null,
      vertical: null,
      subVertical: null,
      serviceLine: null,
      type: null,
      pfpGroupIcp: false,
      techMGroupIcp: false,
    },
    activeSwitch: false,
    loading: false,
  });

  const type = [
    {
      value: t("client.clientDetail.typeOptions.parentCompany"),
      id: "CONTROLLING_COMPANY",
    },
    {
      value: t("client.clientDetail.typeOptions.subsidiaryCompany"),
      id: "CONTROLLED_COMPANY",
    },
    {
      value: t("client.clientDetail.typeOptions.associatedCompany"),
      id: "RELATED_COMPANY",
    },
    {
      value: t("client.clientDetail.typeOptions.thirdPart"),
      id: "THIRD_PARTY",
    },
  ];

  useEffect(() => {
    if (params.id !== "new") {
      loadClient();
      dispatch(
        setSection({
          section: {
            title: `${params.id}`,
          },
        })
      );
    } else {
      dispatch(
        setSection({
          section: {
            title: `${t("client.clientDetail.new")}`,
          },
        })
      );
    }
  }, []);

  const loadClient = async () => {
    dispatch(setLoading(true));
    const client = await ClientPortal.getClients(params.id);
    if (!client.error && client.data) {
      const clientData = {
        id: get(client, "data.id", null),
        name: get(client, "data.name", null),
        group: get(client, "data.group", null),
        billingCountry: get(client, "data.billingCountry", null),
        vertical: get(client, "data.vertical", null),
        subVertical: get(client, "data.subVertical", null),
        serviceLine: get(client, "data.serviceLine", null),
        type: get(client, "data.type", null),
        pfpGroupIcp: get(client, "data.pfpGroupIcp", false),
        techMGroupIcp: get(client, "data.techMGroupIcp", false),
      };

      setState((prevState) => ({
        ...prevState,
        client: clientData,
        activeSwitch: ["CONTROLLING_COMPANY", "CONTROLLED_COMPANY", "RELATED_COMPANY"].includes(clientData.type),
      }));

      formCLientsDetail.setFieldsValue(clientData);
    } else {
      showError(get(client, "error.messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const onFinish = async (values) => {
    const finalValues = {
      ...values,
      pfpGroupIcp: values.pfpGroupIcp ?? false,
      techMGroupIcp: values.techMGroupIcp ?? false,
    };

    setState((prevState) => ({ ...prevState, loading: true }));
    const saveResponse = await ClientPortal.saveClient(finalValues);
    if (!saveResponse.error) {
      showSuccess();
      navigate("/clients");
    } else {
      showError(get(saveResponse, "error.messageCode", "ER000"));
    }
    setState((prevState) => ({ ...prevState, loading: false }));
  };

  const onValuesChange = (changedValues, allValues) => {
    if (changedValues.type) {
      const isSwitchActive = ["CONTROLLING_COMPANY", "CONTROLLED_COMPANY", "RELATED_COMPANY"].includes(allValues.type);
      setState((prevState) => ({
        ...prevState,
        activeSwitch: isSwitchActive,
      }));
    }

    checkFormValidity(allValues);
  };

  const checkFormValidity = (allValues) => {
    const { id, type } = allValues;
    const isValid = !!id && !!type;
    const isIdValid = /^[0-9a-zA-Z]+$/.test(allValues.id);

    if (isValid && isIdValid) {
      setIsFormValid(true);
    } else {
      setIsFormValid(false);
    }
  };

  return (
    <Form
      form={formCLientsDetail}
      layout="vertical"
      initialValues={state.client}
      onFinish={onFinish}
      onValuesChange={onValuesChange}
    >
      <Row gutter={[48, 80]}>
        <Col span={12}>
          <Form.Item
            label={
              <Flex align="start" justify="space-between" className="w-100">
                <strong>
                  {t("client.clientDetail.id")}
                  <Tooltip className="ml-10" title={t("client.clientDetail.infoClientId")}>
                    <InfoCircleOutlined className="icon-small" />
                  </Tooltip>
                </strong>
              </Flex>
            }
            name="id"
            rules={[
              {
                required: true,
                message: t("general.required"),
              },
              {
                pattern: /^[0-9a-zA-Z]+$/,
                message: t("client.clientDetail.infoClientId"),
              },
            ]}
          >
            <Input maxLength={10} placeholder={t("client.clientDetail.id")} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label={<strong>{t("client.clientDetail.name")}</strong>} name="name">
            <Input placeholder={t("client.clientDetail.name")} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label={<strong>{t("client.clientDetail.group")}</strong>} name="group">
            <Input placeholder={t("client.clientDetail.group")} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label={<strong>{t("client.clientDetail.billingCountry")}</strong>} name="billingCountry">
            <Input placeholder={t("client.clientDetail.billingCountry")} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label={<strong>{t("client.clientDetail.vertical")}</strong>} name="vertical">
            <Input placeholder={t("client.clientDetail.vertical")} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label={<strong>{t("client.clientDetail.subVertical")}</strong>} name="subVertical">
            <Input placeholder={t("client.clientDetail.subVertical")} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label={<strong>{t("client.clientDetail.serviceLine")}</strong>} name="serviceLine">
            <Input placeholder={t("client.clientDetail.serviceLine")} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label={<strong>{t("client.clientDetail.type")}</strong>}
            name="type"
            rules={[
              {
                required: true,
                message: t("general.required"),
              },
            ]}
          >
            <Select
              placeholder={t("client.clientDetail.type")}
              showSearch
              options={type.map((item) => ({ label: item.value, value: item.id }))}
            />
          </Form.Item>
        </Col>

        {state.activeSwitch && (
          <>
            <Col span={12}>
              <Form.Item label={<strong>{t("client.clientDetail.pfpGroupIcp")}</strong>} name="pfpGroupIcp">
                <Switch
                  placeholder={t("client.clientDetail.pfpGroupIcp")}
                  disabled={state.client.type === "THIRD_PARTY"}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label={<strong>{t("client.clientDetail.techMGroupIcp")}</strong>} name="techMGroupIcp">
                <Switch
                  placeholder={t("client.clientDetail.techMGroupIcp")}
                  disabled={state.client.type === "THIRD_PARTY"}
                />
              </Form.Item>
            </Col>
          </>
        )}
      </Row>
      <Row justify="end">
        <Form.Item className="btn-submit">
          <PFButton type="primary" htmlType="submit" currentIcon={<SaveOutlined />} isDisabled={!isFormValid} />
        </Form.Item>
      </Row>
    </Form>
  );
};

export default ClientsDetail;
